import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledDropdown,
    Button,
    CardHeader
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactPlayer from 'react-player';
import { Document, Page, pdfjs } from 'react-pdf';
import placeholderImage from '../../../assets/images/placeholder.jpg'; // Relative to src/pages/Projects/ProjectOverview

// Set PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const customStyles = `
  .page-content {
    padding: 48px 0 0 0;
    margin: 0 auto;
    width: 100%;
    max-width: 100%;
  }
  .resource-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin: 0;
    width: 100%;
  }
  .resource-header {
    background: linear-gradient(135deg, #2a4966 0%, #0ab39c 100%);
    color: #ffffff;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding: 1.5rem;
  }
  .resource-header h4 {
    font-weight: 600;
    font-size: 1.4rem;
    color: #ffffff;
    margin: 0;
  }
  .resource-table th, .resource-table td {
    vertical-align: middle;
    padding: 0.75rem;
  }
  .resource-table tr:hover {
    background-color: #f1f5f7;
  }
  .resource-widget-card {
    transition: transform 0.3s ease;
  }
  .resource-widget-card:hover {
    transform: translateY(-5px);
  }
  .action-btn {
    font-size: 0.85rem;
    padding: 0.4rem 0.8rem;
    transition: background-color 0.3s ease, transform 0.2s ease;
  }
  .action-btn:hover {
    transform: translateY(-2px);
  }
  .new-badge {
    margin-left: 0.5rem;
    font-size: 0.75rem;
    vertical-align: middle;
  }
  .dropdown-menu {
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border: none;
  }
  .dropdown-item {
    font-size: 0.85rem;
    padding: 0.5rem 1rem;
    color: #2a4966;
  }
  .dropdown-item:hover {
    background-color: #f1f5f7;
    color: #0ab39c;
  }
  .dropdown-item i {
    margin-right: 0.5rem;
  }
  .resource-modal .modal-content {
    border-radius: 8px;
    border: none;
  }
  .resource-modal .modal-header {
    background: linear-gradient(135deg, #2a4966 0%, #0ab39c 100%);
    color: #ffffff;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding: 1.5rem;
  }
  .resource-modal .modal-header h5 {
    font-weight: 600;
    font-size: 1.4rem;
    color: #ffffff;
  }
  .resource-modal .modal-body {
    padding: 1.5rem;
    background-color: #f9fafb;
  }
  .resource-modal .modal-body p {
    color: #333;
    font-size: 1rem;
    line-height: 1.6;
  }
  .resource-modal .modal-body strong {
    color: #2a4966;
  }
  .resource-player {
    margin: 1rem 0;
    border-radius: 8px;
    overflow: hidden;
    width: 100%;
  }
  .pdf-viewer {
    margin: 1rem 0;
    border: 1px solid #ced4da;
    border-radius: 8px;
    padding: 1rem;
    background-color: #fff;
    width: 100%;
  }
  .pdf-viewer canvas {
    width: 100% !important;
    height: auto !important;
  }
  .download-btn {
    margin-top: 1rem;
  }
`;

const ResourceOverview = ({ resource }) => {
    const [numPages, setNumPages] = useState(null);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const isYouTubeUrl = (url) => {
        try {
            if (!url) return false;
            const regex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
            return regex.test(url);
        } catch {
            return false;
        }
    };

    const handleDownload = (url, filename) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'resource';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    document.title = "Resource Overview | Confidoo";

    if (!resource) {
        return (
            <React.Fragment>
                <style>{customStyles}</style>
                <div className="page-content">
                    <ToastContainer closeButton={false} limit={1} />
                    <p>Loading...</p>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <style>{customStyles}</style>
            <div className="page-content">
                <Container fluid>
                    <ToastContainer closeButton={false} limit={1} />
                    <Row>
                        <Col xl={9} lg={8}>
                            <Card className="resource-card">
                                <CardBody>
                                    <div className="text-muted">
                                        <h6 className="mb-3 fw-semibold text-uppercase">Description</h6>
                                        <div dangerouslySetInnerHTML={{ __html: resource.description }} />

                                        {resource.type === 'vidéo' && resource.fichierOuLien && isYouTubeUrl(resource.fichierOuLien) && (
                                            <div className="pt-3 border-top border-top-dashed mt-4">
                                                <h6 className="mb-3 fw-semibold text-uppercase">Video</h6>
                                                <div className="resource-player">
                                                    <ReactPlayer
                                                        src={resource.fichierOuLien}
                                                        controls
                                                        width="100%"
                                                        height="400px"
                                                        config={{
                                                            youtube: {
                                                                playerVars: {
                                                                    showinfo: 0,
                                                                    rel: 0,
                                                                    modestbranding: 1,
                                                                },
                                                            },
                                                        }}
                                                        onError={(e) => {
                                                            console.error('ReactPlayer Error:', e);
                                                            toast.error('Failed to load YouTube video');
                                                        }}
                                                        onReady={() => console.log('ReactPlayer Ready:', resource.fichierOuLien)}
                                                        onStart={() => console.log('ReactPlayer Started:', resource.fichierOuLien)}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {resource.type === 'pdf' && resource.fichierOuLien && !isYouTubeUrl(resource.fichierOuLien) && (
                                            <div className="pt-3 border-top border-top-dashed mt-4">
                                                <h6 className="mb-3 fw-semibold text-uppercase">PDF Viewer</h6>
                                                <div className="pdf-viewer">
                                                    <Document
                                                        file={resource.fichierOuLien}
                                                        onLoadSuccess={onDocumentLoadSuccess}
                                                        onLoadError={() => toast.error('Failed to load PDF')}
                                                    >
                                                        <Page pageNumber={1} />
                                                    </Document>
                                                    <Button
                                                        color="success"
                                                        className="download-btn"
                                                        onClick={() => handleDownload(resource.fichierOuLien, resource.titre + '.pdf')}
                                                    >
                                                        Download PDF
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {resource.type !== 'vidéo' && resource.type !== 'pdf' && resource.fichierOuLien && !isYouTubeUrl(resource.fichierOuLien) && (
                                            <div className="pt-3 border-top border-top-dashed mt-4">
                                                <h6 className="mb-3 fw-semibold text-uppercase">Attachment</h6>
                                                <div className="border rounded border-dashed p-2">
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-shrink-0 me-3">
                                                            <div className="avatar-sm">
                                                                <div className="avatar-title bg-light text-secondary rounded fs-24 shadow">
                                                                    <i className="ri-file-line"></i>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex-grow-1 overflow-hidden">
                                                            <h5 className="fs-13 mb-1">
                                                                <Link to="#" className="text-body text-truncate d-block">
                                                                    {resource.titre}
                                                                </Link>
                                                            </h5>
                                                            <div>File Attachment</div>
                                                        </div>
                                                        <div className="flex-shrink-0 ms-2">
                                                            <div className="d-flex gap-1">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon text-muted btn-sm fs-18 shadow-none"
                                                                    onClick={() => handleDownload(resource.fichierOuLien, resource.titre)}
                                                                >
                                                                    <i className="ri-download-2-line"></i>
                                                                </button>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {resource.openGraphImage && (
                                            <div className="pt-3 border-top border-top-dashed mt-4">
                                                <h6 className="mb-3 fw-semibold text-uppercase">Open Graph Image</h6>
                                                <div className="border rounded border-dashed p-2">
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-shrink-0 me-3">
                                                            <div className="avatar-sm">
                                                                <img
                                                                    src={resource.openGraphImage || placeholderImage}
                                                                    alt={resource.titre}
                                                                    className="img-fluid rounded"
                                                                    onError={(e) => { e.target.src = placeholderImage; }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex-grow-1 overflow-hidden">
                                                            <h5 className="fs-13 mb-1">
                                                                <Link to="#" className="text-body text-truncate d-block">
                                                                    Thumbnail Image
                                                                </Link>
                                                            </h5>
                                                            <div>Preview Image</div>
                                                        </div>
                                                        <div className="flex-shrink-0 ms-2">
                                                            <div className="d-flex gap-1">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon text-muted btn-sm fs-18 shadow-none"
                                                                    onClick={() => handleDownload(resource.openGraphImage || placeholderImage, 'thumbnail')}
                                                                >
                                                                    <i className="ri-download-2-line"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={3} lg={4}>
                            <Card>
                                <CardHeader className="align-items-center d-flex border-bottom-dashed">
                                    <h4 className="card-title mb-0 flex-grow-1">Tags</h4>
                                </CardHeader>
                                <CardBody>
                                    <div className="d-flex flex-wrap gap-2 fs-16">
                                        {resource.tags && resource.tags.length > 0 ? (
                                            resource.tags.map((tag, index) => (
                                                <div key={index} className="badge fw-medium badge-soft-secondary">
                                                    {tag}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted">No tags available</p>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>

                            <Card>
                                <CardHeader className="align-items-center d-flex border-bottom-dashed">
                                    <h4 className="card-title mb-0 flex-grow-1">SEO Details</h4>
                                </CardHeader>
                                <CardBody>
                                    <div className="vstack gap-2">
                                        <div>
                                            <p className="mb-2 text-uppercase fw-medium">SEO Title:</p>
                                            <h5 className="fs-13 mb-0">{resource.seoTitle || 'N/A'}</h5>
                                        </div>
                                        <div>
                                            <p className="mb-2 text-uppercase fw-medium">Meta Description:</p>
                                            <h5 className="fs-13 mb-0">{resource.metaDescription || 'N/A'}</h5>
                                        </div>
                                        <div>
                                            <p className="mb-2 text-uppercase fw-medium">URL Propre:</p>
                                            <h5 className="fs-13 mb-0">{resource.urlPropre || 'N/A'}</h5>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default ResourceOverview;